
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {LeverageProfileDeleteResponse} from './LeverageProfileDeleteResponse';

/**
 * LeverageProfileDeleteRequest struct
 */
class LeverageProfileDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} leverageProfileId
   * @param {string=} leverageProfileName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), leverageProfileId = null, leverageProfileName = null) {
    super()
    super.copy(parent)
    this.LeverageProfileId = leverageProfileId
    this.LeverageProfileName = leverageProfileName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LeverageProfileDeleteRequest}
   * @param {!LeverageProfileDeleteRequest} other Other struct
   * @returns {!LeverageProfileDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.LeverageProfileId != null) {
      this.LeverageProfileId = new UUID(other.LeverageProfileId)
    } else {
      this.LeverageProfileId = null
    }
    if (other.LeverageProfileName != null) {
      this.LeverageProfileName = other.LeverageProfileName
    } else {
      this.LeverageProfileName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LeverageProfileDeleteRequest}
   * @returns {!LeverageProfileDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LeverageProfileDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LeverageProfileDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LeverageProfileDeleteRequest}
   * @param {!LeverageProfileDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LeverageProfileDeleteRequest)) {
      throw new TypeError('Instance of LeverageProfileDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LeverageProfileDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      LeverageProfileId: ((this.LeverageProfileId != null) ? this.LeverageProfileId.toString() : null),
      LeverageProfileName: ((this.LeverageProfileName != null) ? this.LeverageProfileName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LeverageProfileDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LeverageProfileDeleteRequest} other Object value
   * @returns {!LeverageProfileDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new LeverageProfileDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LeverageProfileDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LeverageProfileDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LeverageProfileDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6381
  }
}

export { LeverageProfileDeleteRequest };

/**
 * Fast Binary Encoding LeverageProfileDeleteRequest field model
 */
class FieldModelLeverageProfileDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.LeverageProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.LeverageProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.LeverageProfileId.fbeOffset + this.LeverageProfileId.fbeSize), buffer, this.LeverageProfileId.fbeOffset + this.LeverageProfileId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.LeverageProfileId.fbeSize + this.LeverageProfileName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.LeverageProfileId.fbeExtra + this.LeverageProfileName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLeverageProfileDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6381
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.LeverageProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LeverageProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LeverageProfileId.fbeSize

    if ((fbeCurrentSize + this.LeverageProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LeverageProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LeverageProfileName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {!LeverageProfileDeleteRequest} fbeValue Default value, defaults is new LeverageProfileDeleteRequest()
   * @returns {!LeverageProfileDeleteRequest} LeverageProfileDeleteRequest value
   */
  get (fbeValue = new LeverageProfileDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {!LeverageProfileDeleteRequest} fbeValue LeverageProfileDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.LeverageProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.LeverageProfileId = this.LeverageProfileId.get()
    } else {
      fbeValue.LeverageProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LeverageProfileId.fbeSize

    if ((fbeCurrentSize + this.LeverageProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.LeverageProfileName = this.LeverageProfileName.get()
    } else {
      fbeValue.LeverageProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LeverageProfileName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {!LeverageProfileDeleteRequest} fbeValue LeverageProfileDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLeverageProfileDeleteRequest}
   * @param {LeverageProfileDeleteRequest} fbeValue LeverageProfileDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.LeverageProfileId.set(fbeValue.LeverageProfileId)
    this.LeverageProfileName.set(fbeValue.LeverageProfileName)
  }
}

export { FieldModelLeverageProfileDeleteRequest };

/**
 * Fast Binary Encoding LeverageProfileDeleteRequest model
 */
class LeverageProfileDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLeverageProfileDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the LeverageProfileDeleteRequest model
   * @this {!LeverageProfileDeleteRequestModel}
   * @returns {!FieldModelLeverageProfileDeleteRequest} model LeverageProfileDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LeverageProfileDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LeverageProfileDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LeverageProfileDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LeverageProfileDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLeverageProfileDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LeverageProfileDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LeverageProfileDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LeverageProfileDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LeverageProfileDeleteRequestModel}
   * @param {!LeverageProfileDeleteRequest} value LeverageProfileDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LeverageProfileDeleteRequestModel}
   * @param {!LeverageProfileDeleteRequest} value LeverageProfileDeleteRequest value, defaults is new LeverageProfileDeleteRequest()
   * @return {!object} Deserialized LeverageProfileDeleteRequest value and its size
   */
  deserialize (value = new LeverageProfileDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LeverageProfileDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LeverageProfileDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LeverageProfileDeleteRequestModel}
   * @param {!number} prev Previous LeverageProfileDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LeverageProfileDeleteRequestModel };
LeverageProfileDeleteRequest.__has_response = true;
LeverageProfileDeleteRequest.__response_class = LeverageProfileDeleteResponse;
